// src/App.js
import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ApolloProvider } from "@apollo/client";
import client from "./apolloClient";

import Dashboard from "./pages/Dashboard";
import DashboardTutor from "./pages/DashboardTutor";
import Menu from "./pages/Menu";
import Login from "./pages/Login";
import Contact from "./pages/Contact";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import AboutUs from "./pages/AboutUs";
import Klausuren from "./pages/Klausuren";
import CheckGtm1 from "./temp-close/CheckGtm1";
import SubjectClosed from "./temp-close/SubjectClosed";
import SubjectRouteGuard from "./temp-close/SubjectRouteGuard";
import PhaseTwo from "./temp-close/PhaseTwo";
import PhaseTwoFaq from "./temp-close/PhaseTwoFaq";

import SubjectExp1 from "./pages/SubjectExp1";
import SMiddlePage from "./pages/SMiddlePage";
import SMiddlePageTutor from "./pages/SMiddlePageTutor";
import SubjectTutorL from "./pages/SubjectTutorL";

import Header from "./components/Header";
import Footer from "./components/Footer";
import TutorViewToggle from "./components/TutorViewToggle";
import TestPhaseAnnouncement from "./components/TestPhaseAnnouncement";
import {
  AUTH_CHANGE_EVENT,
  AUTH_EVENT_KEY,
  AUTH_STORAGE_KEYS,
  clearExpiredAuthSession,
  getAuthExpiryMs,
  getDefaultAuthedRoute,
  isAuthenticated,
} from "./utils/authSession";

const PUBLIC_PATHS = new Set([
  "/",
  "/4mohanad",
  "/contact",
  "/terms",
  "/privacy",
  "/aboutus",
  "/phase-two",
  "/phase-two/",
  "/phase-two/faq",
]);

const isTutorUser = () => {
  const role = localStorage.getItem("userRole");
  return role === "TUTOR" || role === "ADMIN";
};

const mapTutorPathToStudentPath = (pathname) => {
  if (!pathname) return "/dashboard";

  if (pathname === "/dashboard-tutor") {
    return "/dashboard";
  }

  const tutorMiddleMatch = pathname.match(/^\/subject-tutor\/([^/]+)\/?$/);
  if (tutorMiddleMatch) {
    const slug = tutorMiddleMatch[1];
    return `/subject/${slug}`;
  }

  const tutorLearnMatch = pathname.match(/^\/subject-tutor\/([^/]+)\/learn(\/.*)?$/);
  if (tutorLearnMatch) {
    const slug = tutorLearnMatch[1];
    const rest = tutorLearnMatch[2] || "";
    return `/subject/${slug}/learn${rest}`;
  }

  const tutorEditMatch = pathname.match(/^\/subject\/([^/]+)\/edit(\/.*)?$/);
  if (tutorEditMatch) {
    const slug = tutorEditMatch[1];
    const rest = tutorEditMatch[2] || "";
    return `/subject/${slug}/learn${rest}`;
  }

  return "/dashboard";
};

// ---- simple auth gate ----
function RequireAuth({ children }) {
  const location = useLocation();
  if (!isAuthenticated()) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  return children;
}

function RequireGuest({ children }) {
  if (isAuthenticated()) {
    return <Navigate to={getDefaultAuthedRoute()} replace />;
  }
  return children;
}

function RequireTutor({ children }) {
  const location = useLocation();

  if (!isAuthenticated()) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  if (!isTutorUser()) {
    const fallbackPath = mapTutorPathToStudentPath(location.pathname);
    const suffix = `${location.search || ""}${location.hash || ""}`;
    return <Navigate to={`${fallbackPath}${suffix}`} replace />;
  }

  return children;
}

function AuthRouteSync() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const isPublicPath = (pathname) =>
      PUBLIC_PATHS.has(pathname) || pathname.startsWith("/subject-example");
    let expiryTimerId = null;

    const syncByAuthState = () => {
      clearExpiredAuthSession();
      const authed = isAuthenticated();
      if (!authed && !isPublicPath(location.pathname)) {
        navigate("/", { replace: true });
        scheduleExpiryTimer();
        return;
      }
      scheduleExpiryTimer();
    };

    const clearExpiryTimer = () => {
      if (expiryTimerId) {
        window.clearTimeout(expiryTimerId);
        expiryTimerId = null;
      }
    };

    const scheduleExpiryTimer = () => {
      clearExpiryTimer();
      const expiryMs = getAuthExpiryMs();
      if (!expiryMs) return;

      const delayMs = Math.max(0, expiryMs - Date.now() + 250);
      expiryTimerId = window.setTimeout(syncByAuthState, delayMs);
    };

    const onStorage = (e) => {
      if (!e.key) {
        syncByAuthState();
        return;
      }
      if (e.key === AUTH_EVENT_KEY || AUTH_STORAGE_KEYS.includes(e.key)) {
        syncByAuthState();
      }
    };

    const onAuthChanged = () => syncByAuthState();

    syncByAuthState();
    window.addEventListener("storage", onStorage);
    window.addEventListener(AUTH_CHANGE_EVENT, onAuthChanged);
    return () => {
      clearExpiryTimer();
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(AUTH_CHANGE_EVENT, onAuthChanged);
    };
  }, [location.pathname, navigate]);

  return null;
}

function App() {
  const PageWithLayout = ({ children }) => (
    <>
      <Header />
      <div className="page-content">{children}</div>
      <Footer />
    </>
  );

  return (
    <ApolloProvider client={client}>
      <Router>
        <AuthRouteSync />
        <Routes>

          {/* Login */}
          <Route
            path="/"
            element={
              <RequireGuest>
                <Login />
              </RequireGuest>
            }
          />
          <Route
            path="/4mohanad"
            element={
              <RequireGuest>
                <Login />
              </RequireGuest>
            }
          />

          {/* Public Phase Two pages */}
          <Route path="/phase-two" element={<PhaseTwo />} />
          <Route path="/phase-two/faq" element={<PhaseTwoFaq />} />

          {/* Dashboard (Student) */}
          <Route
            path="/dashboard"
            element={
              <RequireAuth>
                <PageWithLayout>
                  <Dashboard />
                </PageWithLayout>
              </RequireAuth>
            }
          />

          {/* Dashboard (Tutor/Admin) */}
          <Route
            path="/dashboard-tutor"
            element={
              <RequireTutor>
                <PageWithLayout>
                  <DashboardTutor />
                </PageWithLayout>
              </RequireTutor>
            }
          />

          {/* Menu */}
          <Route
            path="/menu"
            element={
              <RequireAuth>
                <PageWithLayout>
                  <Menu />
                </PageWithLayout>
              </RequireAuth>
            }
          />

          {/* Klausuren listing */}
          <Route
            path="/klausuren"
            element={
              <RequireAuth>
                <PageWithLayout>
                  <Klausuren />
                </PageWithLayout>
              </RequireAuth>
            }
          />

          {/* Tutor-only GTM I check page */}
          <Route
            path="/check-gtm-1"
            element={
              <RequireTutor>
                <PageWithLayout>
                  <CheckGtm1 />
                </PageWithLayout>
              </RequireTutor>
            }
          />

          {/* Temporarily closed subject notice */}
          <Route
            path="/subject-closed/:subjectId"
            element={
              <RequireAuth>
                <PageWithLayout>
                  <SubjectClosed />
                </PageWithLayout>
              </RequireAuth>
            }
          />

          {/* Contact */}
          <Route
            path="/contact"
            element={
              <PageWithLayout>
                <Contact />
              </PageWithLayout>
            }
          />

          {/* Terms */}
          <Route
            path="/terms"
            element={
              <PageWithLayout>
                <Terms />
              </PageWithLayout>
            }
          />

          {/* Privacy */}
          <Route
            path="/privacy"
            element={
              <PageWithLayout>
                <Privacy />
              </PageWithLayout>
            }
          />

          {/* About Us */}
          <Route
            path="/aboutus"
            element={
              <PageWithLayout>
                <AboutUs />
              </PageWithLayout>
            }
          />

          {/* -----------------------------
              Subject Middle Pages
             ----------------------------- */}

          {/* Student middle page by slug */}
          <Route
            path="/subject/:subjectSlug"
            element={
              <RequireAuth>
                <SubjectRouteGuard>
                  <PageWithLayout>
                    <SMiddlePage />
                  </PageWithLayout>
                </SubjectRouteGuard>
              </RequireAuth>
            }
          />

          {/* Tutor middle page by slug (still supported) */}
          <Route
            path="/subject-tutor/:subjectSlug"
            element={
              <RequireTutor>
                <SubjectRouteGuard>
                  <PageWithLayout>
                    <SMiddlePageTutor />
                  </PageWithLayout>
                </SubjectRouteGuard>
              </RequireTutor>
            }
          />

          {/* -----------------------------
              Student "learn" pages
             ----------------------------- */}
          <Route
            path="/subject/:subjectSlug/learn"
            element={
              <RequireAuth>
                <SubjectRouteGuard>
                  <SubjectExp1 />
                </SubjectRouteGuard>
              </RequireAuth>
            }
          />
          <Route
            path="/subject/:subjectSlug/learn/:lessonSlug"
            element={
              <RequireAuth>
                <SubjectRouteGuard>
                  <SubjectExp1 />
                </SubjectRouteGuard>
              </RequireAuth>
            }
          />

          {/* -----------------------------
              ✅ Tutor "edit" pages (NEW correct pattern)
             ----------------------------- */}
          <Route
            path="/subject/:subjectSlug/edit"
            element={
              <RequireTutor>
                <SubjectRouteGuard>
                  <SubjectTutorL />
                </SubjectRouteGuard>
              </RequireTutor>
            }
          />
          <Route
            path="/subject/:subjectSlug/edit/:lessonSlug"
            element={
              <RequireTutor>
                <SubjectRouteGuard>
                  <SubjectTutorL />
                </SubjectRouteGuard>
              </RequireTutor>
            }
          />

          {/* -----------------------------
              Backwards compatibility (old tutor learn routes)
              If you still hit them, you can keep them for now
             ----------------------------- */}
          <Route
            path="/subject-tutor/:subjectSlug/learn"
            element={
              <RequireTutor>
                <SubjectRouteGuard>
                  <SubjectExp1 />
                </SubjectRouteGuard>
              </RequireTutor>
            }
          />
          <Route
            path="/subject-tutor/:subjectSlug/learn/:lessonSlug"
            element={
              <RequireTutor>
                <SubjectRouteGuard>
                  <SubjectExp1 />
                </SubjectRouteGuard>
              </RequireTutor>
            }
          />

          {/* Keep your old example routes */}
          <Route path="/subject-example" element={<SubjectExp1 />} />
          <Route path="/subject-example/:lessonSlug" element={<SubjectExp1 />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        {/* Global floating toggle */}
        <TutorViewToggle />
        <TestPhaseAnnouncement />
      </Router>
    </ApolloProvider>
  );
}

export default App;
